import { Box, Typography, useTheme, Paper } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import mime from "mime";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiBookOpen, FiClipboard, FiClock, FiFile, FiMapPin } from "react-icons/fi";
import FileUpload from "../components/input/FileUpload";
import ImageUpload from "../components/input/ImageUpload";
import ResizableTextBox from "../components/input/ResizableTextBox";
import TextBox from "../components/input/TextBox";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { Project } from "../db_access/project";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";
import muiSytle from "../style/components-style/muiStyle";
import SupplierInfo from "./SupplierInfo";

const ProjectInfo = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { selectedProjectIds, isSaveClicked, setIsSaveClicked, setIsSaveVisible, setRefresh } = useContextProvider();
  const [formData, setFormData] = useState({
    extendedServiceLife: 0,
    id: "",
    intro: "",
    isEpd: false,
    place_of_manufacture: "",
    place_of_use: "",
    service_life: 0,
    extended_service_life: 0,
    specifications: "",
    uuid: "",
  });
  const [photo, setPhoto] = useState();
  const [photoPreview, setPhotoPreview] = useState();
  const [specDocument, setSpecDocument] = useState();
  const [specDocumentPreview, setSpecDocumentPreview] = useState();
  const [safetyDocument, setSafetyDocument] = useState();
  const [safetyDocumentPreview, setSafetyDocumentPreview] = useState();
  const [modified, setModified] = useState(false);
  const [selectedProject, setSelectedProject] = useState();
  const ref = useRef(null);

  useEffect(() => {
    if (selectedProjectIds.length < 1) {
      setSelectedProject(undefined);
    } else {
      Project.get_by_id(selectedProjectIds[0])
        .then((res) => res.json())
        .then((data) => {
          setSelectedProject(data[0]);
        });
    }
  }, [selectedProjectIds]);

  useEffect(() => {
    if (selectedProject) {
      const isEpdValue = selectedProject?.is_epd === 1;
      setFormData((prev) => ({
        ...prev,
        id: selectedProject?.id,
        service_life: selectedProject?.service_life || "",
        intro: selectedProject?.intro || "",
        specifications: selectedProject?.specifications || "",
        place_of_manufacture: selectedProject?.place_of_manufacture || "",
        place_of_use: selectedProject?.place_of_use || "",
        uuid: selectedProject?.uuid || "",
        extended_service_life: selectedProject?.extended_service_life || "",
        isEpd: isEpdValue,
      }));
    }
  }, [selectedProject]);

  const handleChange = (field) => (e) => {
    if (formData.isEpd) return;
    const value = e.target.value;
    setFormData({ ...formData, [field]: value });
    setIsSaveVisible(true);
    setModified(true);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
    } else {
      setPhoto(undefined);
    }
    setIsSaveVisible(true);
    setModified(true);
  };

  const handleSpecDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSpecDocument(file);
    } else {
      setSpecDocument(undefined);
    }
    setIsSaveVisible(true);
    setModified(true);
  };

  const handleSafetyDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSafetyDocument(file);
    } else {
      setSafetyDocument(undefined);
    }
    setIsSaveVisible(true);
    setModified(true);
  };

  useEffect(() => {
    if (photo) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(photo);
    } else {
      setPhotoPreview(undefined);
    }
  }, [photo]);
  useEffect(() => {
    if (specDocument) {
      setSpecDocumentPreview("spec_document." + mime.getExtension(specDocument.type));
    } else {
      setSpecDocumentPreview(undefined);
    }
  }, [specDocument]);
  useEffect(() => {
    if (safetyDocument) {
      setSafetyDocumentPreview("safety_document." + mime.getExtension(safetyDocument.type));
    } else {
      setSafetyDocumentPreview(undefined);
    }
  }, [safetyDocument]);

  useEffect(() => {
    if (selectedProject && selectedProject.id) {
      Project.get_photo(selectedProject.id)
        .then((res) => {
          if (res.ok) {
            return res.blob();
          } else {
            return undefined;
          }
        })
        .then((j) => {
          setPhoto(j);
        });
    } else {
      setPhoto(undefined);
    }
  }, [selectedProject]);
  useEffect(() => {
    if (selectedProject && selectedProject.id) {
      Project.get_spec_document(selectedProject.id)
        .then((res) => {
          if (res.ok) {
            return res.blob();
          } else {
            return undefined;
          }
        })
        .then((j) => {
          setSpecDocument(j);
        });
    } else {
      setSpecDocument(undefined);
    }
  }, [selectedProject]);
  useEffect(() => {
    if (selectedProject && selectedProject.id) {
      Project.get_safety_document(selectedProject.id)
        .then((res) => {
          if (res.ok) {
            return res.blob();
          } else {
            return undefined;
          }
        })
        .then((j) => {
          setSafetyDocument(j);
        });
    } else {
      setSafetyDocument(undefined);
    }
  }, [selectedProject]);
  useEffect(() => {
    if (isSaveClicked && modified) {
      const fd = new FormData();
      Object.keys(formData).forEach((k) => {
        fd.append(k, formData[k]);
      });
      fd.append("photo", photo);
      fd.append("specDocument", specDocument);
      fd.append("safetyDocument", safetyDocument);
      Project.save_project_info(fd)
        .catch((err) => {
          console.error("Error:", err);
        })
        .finally(() => {
          setIsSaveClicked(false);
          setIsSaveVisible(false);
          setModified(false);
          setRefresh((prev) => !prev);
        });
    }
  }, [isSaveClicked, modified]);

  return (
    <Paper variant="Table Container">
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("Project Info")}</Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }} ref={ref}>
          {/* LEFT COLUMN: Input Fields */}
          <Box sx={{ flex: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <ResizableTextBox
              Icon={FiBookOpen}
              label={"Project Introduction"}
              placeholder={"Project Introduction"}
              value={formData.intro}
              onChange={handleChange("intro")}
              disabled={formData.isEpd}
            />
            <ResizableTextBox
              Icon={FiClipboard}
              label={"Specifications"}
              placeholder={"Specifications"}
              value={formData.specifications}
              onChange={handleChange("specifications")}
              disabled={formData.isEpd}
            />
            <TextBox
              Icon={FiClipboard}
              label={"Project UUID"}
              placeholder={`Project UUID`}
              value={formData.uuid}
              onChange={handleChange("uuid")}
              disabled={formData.isEpd}
            />
            <TextBox
              Icon={FiClock}
              label={"Service Life (years)"}
              placeholder={"Years"}
              value={formData.service_life}
              onChange={handleChange("service_life")}
              disabled={formData.isEpd}
            />
            <TextBox
              Icon={FiClock}
              label={"Extended Service Life (years)"}
              placeholder={"Years"}
              value={formData.extended_service_life}
              onChange={handleChange("extended_service_life")}
              disabled={formData.isEpd}
            />
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <TextBox
                  Icon={FiMapPin}
                  label={"Place of Manufacture"}
                  placeholder="Place of Manufacture"
                  value={formData.place_of_manufacture}
                  onChange={handleChange("place_of_manufacture")}
                  disabled={formData.isEpd}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextBox
                  Icon={FiMapPin}
                  label={"Place of Use"}
                  placeholder="Place of Use"
                  value={formData.place_of_use}
                  onChange={handleChange("place_of_use")}
                  disabled={formData.isEpd}
                />
              </Box>
            </Box>
          </Box>

          {/* RIGHT COLUMN: Media & Documents */}
          <Box sx={{ flex: 1, maxWidth: { md: 400 }, width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
            <ImageUpload
              Icon={FiFile}
              label={"Project Photo"}
              placeholder={"Click to upload photo"}
              onChange={handlePhotoChange}
              disabled={formData.isEpd}
              preview={photoPreview}
              alt={"Project"}
              accept={"image/*"}
              deletePreview={() => {
                setPhoto(undefined);
                setIsSaveVisible(true);
                setModified(true);
              }}
            />
            <FileUpload
              Icon={FiFile}
              label={"Specification Document"}
              preview={specDocumentPreview}
              placeholder={"Click to upload document"}
              onChange={handleSpecDocumentChange}
              disabled={formData.isEpd}
              accept={".pdf,.doc,.docx"}
              deletePreview={() => {
                setSpecDocument(undefined);
                setIsSaveVisible(true);
                setModified(true);
              }}
              file={specDocument}
            />
            <FileUpload
              Icon={FiFile}
              label={"Project Safety"}
              preview={safetyDocumentPreview}
              placeholder={"Click to upload document"}
              onChange={handleSafetyDocumentChange}
              disabled={formData.isEpd}
              accept={".pdf,.doc,.docx"}
              deletePreview={() => {
                setSafetyDocument(undefined);
                setIsSaveVisible(true);
                setModified(true);
              }}
              file={safetyDocument}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default ProjectInfo;
